import type { StringKeyed } from '../../types/util'
import type { Alternatives, DocSearchTerm, Sense, SerializedPhrase } from '../../util/terms'
import type { DAStudy, DDStudy, TaggedSentence } from '../../types/portal'
import type { Quantifier } from '../../types/quantifier'

// Annotations

export interface Range {
	start: number
	end: number
	kind?: string
}

export interface TypedRange extends Range {
	i?: number
	text: string
	captured?: string
	parsed?: any
	terms?: DocSearchTerm[]
	senses?: Sense[]
	source?: string
	type?: string
	types?: string[]
	deleted?: boolean
	parent?: any
	visual?: boolean
	interactive?: boolean
	primary?: boolean
	measure?: any
	measures?: Annotation[]
	related?: Annotation[]
	groupn?: any
	hr?: any
	ci?: any
	pvalue?: any
	layer?: number
	weight?: number
}

export interface TextRange extends TypedRange {
	kind: 'text'
}

export interface Label extends TypedRange {
	kind: 'label'
	parent?: Section
}

export interface Section extends TypedRange {
	kind: 'section'
	label: string
	visual: false
	labelRef?: Label
	sentences?: Sentence[]
}

export interface Sentence extends TypedRange {
	kind: 'sentence'
	visual: false
}

export interface Annotation extends TypedRange {
	section?: Section
	sentence?: Sentence
}

export interface StudyAnnotations {
	title: Annotation[]
	abstract: Annotation[]
}

// Abstract

export interface AbstractSource {
	Title?: string
	Source?: string
	Private?: boolean
	PublicationYear?: string
	Authors?: string
	Acronym?: string
	DOI?: string
	FullTextURL?: string
	Designs?: string[]
	AbstractSentences?: TaggedSentence[]
	Abstract?: string
	N?: string
}

// DA types

interface AscribedExtractor {
	extractor?: number
	client?: number
	isAutomatedImport?: boolean
}

export interface DATreatment {
	Id: string
	Phase: string
	Dosages: any[]
}
export interface DAIntervention {
	Id?: string
	name?: string
	termIds: Alternatives
	terms?: string[][]
	Treatments?: DATreatment[]
}

interface DDGroup {}

export interface DAStudyGroup extends DDGroup, AscribedExtractor {
	Id?: string
	RefId?: string
	Name?: string
	OriginalName?: string
	N?: number
	age?: number
	ageSD?: number
	male?: number
	Interventions: DAIntervention[]
}
export { DAStudy }
export interface DAStudyWithMetadata extends DAStudy {
	metadata: any
}

export interface DAStudyRow extends AscribedExtractor {
	id?: string
	name: string
	type: string
	dataType: ValueType
	unit: string
	cells?: DACell[]
	termIds: Alternatives
	time?: {
		days?: number
		description?: string
	}
	matchingTerm?: Sense
	isPositive?: boolean
	definition?: string
	method?: string
}
export type DAStudyOutcome = DAStudyRow
export interface DACell extends AscribedExtractor {
	Id?: string
	group: string
	N?: number
	count?: number
	number?: number
	value?: any
	type?: 'pct' | 'count'
	sd?: number
	se?: number
	note?: string
}

export interface REFullStudy extends Omit<DDStudy, 'conditions'> {
	Phases: any[]
	extractions: SummarizedExtraction[]
	studyLevel: DAStudyRow[]
	groupLevel: DAStudyRow[]
	characteristics: DAStudyRow[]
	outcomes: DAStudyRow[]
	conditions: Alternatives[]
	Sections: any
}

// Extraction view model

export enum FieldType {
	BINARY = 'Binary',
	CONTINUOUS = 'Continuous',
	COUNT = 'Count',
	TEXT = 'Text'
}

export enum RowType {
	PCT_CHANGE = '% Change',
	CHANGE = 'Change',
	DURATION = 'Duration',
	TIME_SINCE = 'Time Since',
	TIME_TO = 'Time to'
}

export enum ValueType {
	AVERAGE = 'Average',
	MEAN = 'Mean',
	MEDIAN = 'Median',
	PARTICIPANT = 'Participant',
	INTEGER = 'Integer',
	STRING = 'String'
}

/**
 * FieldType/RowType(/ValueType) combinations understood and supported by RE
 */
export const SUPPORTED_ROW_TYPES = [{
	fieldType: FieldType.BINARY,
	rowType: null // Standard
}, {
	fieldType: FieldType.CONTINUOUS,
	rowType: null, // Standard
	label: 'Average',
}, {
	fieldType: FieldType.CONTINUOUS,
	rowType: RowType.CHANGE,
	label: RowType.CHANGE
}, {
	fieldType: FieldType.CONTINUOUS,
	rowType: RowType.PCT_CHANGE,
	label: RowType.PCT_CHANGE
}, {
	fieldType: FieldType.COUNT,
	rowType: null,
	label: 'Event'
}]

enum SD {
	SD = 'sd',
	SE = 'se'
}

export const DEV_TYPES = Object.values(SD)

export const STUDY_LEVEL_GRP = 'null'

export interface EQuantifier {
	val: string
	quantifier?: Quantifier
	num?: string
	sd?: SD
	unit?: string
	a?: Annotation
}
export type EDosage = EQuantifier

export interface ETimepoint extends EQuantifier {
	exists?: boolean
	a?: Annotation
}

export interface ETerm {
	label: string
	lt?: number
	senses?: SerializedPhrase[]
}
export interface EName {
	val: string
	term?: ETerm
	a?: Annotation
}

export interface ETreatment {
	id: string
	measure: EDosage
}

export interface ENValue {
	val: string
	a?: Annotation
}

export interface EIntervention {
	id: string
	existing?: EIntervention
	name: EName
	treatments: ETreatment[]
}

export interface EGroup {
	id: string
	refid?: string
	name?: string
	existing?: EGroup
	matching?: EGroup
	n?: ENValue
	subgroup?: string
	interventions: EIntervention[]
	chars?: any[]
	isRE?: boolean
}

export interface ERange {
	type?: string
	percent?: number
	low: number
	high: number
}
export interface ECell {
	id?: string
	group?: string
	row: string
	val: string
	pct?: boolean
	sd?: string
	n?: ENValue
	existing?: ECell
	advanced?: boolean
	a?: Annotation
	range?: ERange
}

export interface ERow {
	id: string
	existing?: ERow
	rowConfig: any
	name: EName
	section: string
	type: string
	rowType: string
	devType?: SD
	valueType?: string
	initIsPositive?: boolean
	isPositive?: boolean
	timepoint?: ETimepoint
	unit?: string
	cells?: StringKeyed<ECell>
	definition?: string
	method?: string
}

export interface Extraction {
	analysisType?: string
	importSource?: string
	outcomes: ERow[]
	studyLevel?: ERow[]
	groupLevel?: ERow[]
	characteristics?: ERow[]
	groups: EGroup[]
	studyN?: ENValue
	note?: string
}

export interface ExtractionRequest {
	StudyId: number
	ArticleId: string
	Updates: ExtractionUpdate[]
}

type StudyMetadata = any
interface Summary extends StudyMetadata {
	// preserve DocSearch-originated tagged abstract sentences for use in creation of new document
	AbstractSentences: TaggedSentence[]
}

type ExtractedData = any
interface ExtractionDataV1 extends ExtractedData {
	StudyN: number
	AbstractSentences: TaggedSentence[]
}
interface ExtractionDataV2 extends ExtractionDataV1 {
	Summary: Summary
	//AbstractSentences?: TaggedSentence[]
}
interface ExtractionDataV3 extends ExtractedData {
	Summary?: Summary
	Private?: boolean
	StudyN?: number
	AbstractSentences?: TaggedSentence[]
}
export type ExtractionData = ExtractionDataV3

export interface ExtractionUpdate {
	Id: number
	User: User['Name']
	UserId: User['Id']
	Date: number
	Data: ExtractionData
	Collection?: string
	ExtractGroup?: string
	ExtractGroupIndex?: number
	Note?: string
	Upload?: boolean
	Action?: string
}

export interface SummarizedExtraction extends ExtractionUpdate {
	IsFromHistory: boolean
	IsCommunity: boolean
	ClientString: string
	Loadable: boolean,
	Data: ExtractionUpdate['Data'] & {
		Sections: any
	}
}

export interface ExtractorMetadata {
	User: { Id: number, Name: string }
	Client: { Id: number, Name: string }
	Division: { Name: string }
}